import { RpcRequest, RpcRequestTimeout, RpcResponse } from './common'
import uuid from '../../utils/uuid/uuid'
import getMessageOrigin from '../../utils/getMessageOrigin'

export const REQUEST_TIMEOUT = 20000

export interface RpcCallback {
  (result: any, error?: Error): void
}

interface RpcCallbackData {
  callback: RpcCallback
  timeoutHandle?: number
}

interface RpcCallbacks {
  [id: string]: RpcCallbackData
}

export class RpcClient {
  private window: Window

  private targetWindow: Window

  private namespace: string

  private targetOrigin: string

  private listenerAttached = false

  private callbacks: RpcCallbacks = {}

  private closeInterstitialCallback: () => void

  public constructor(window: Window, targetWindow: Window, namespace: string, targetOrigin: string) {
    this.window = window
    this.targetWindow = targetWindow
    this.namespace = namespace
    this.targetOrigin = targetOrigin
  }

  // eslint-disable-next-line default-param-last
  public call(endpoint: string, args: any[] = [], callback?: RpcCallback, wait = true, closeInterstitialCallback?: () => void) {
    if (!this.listenerAttached) {
      this.window.addEventListener('message', (event: MessageEvent) => this.handleMessage(event), false)
      this.listenerAttached = true
    }

    const request: RpcRequest = {
      id: uuid(),
      namespace: this.namespace,
      endpoint,
      arguments: args
    }

    if (callback) {
      if (wait) {
        this.callbacks[request.id] = {
          callback,
          timeoutHandle: window.setTimeout(() => {
            const callbackData = this.callbacks[request.id]
            if (!callbackData) {
              return
            }

            delete this.callbacks[request.id]
            callbackData.callback(undefined, new RpcRequestTimeout())
          }, REQUEST_TIMEOUT)
        }
      } else {
        this.callbacks[request.id] = { callback }
      }
    }

    if (
      request.endpoint === 'openHostedInterstitial'
    ) {
      this.closeInterstitialCallback = closeInterstitialCallback
      this.window.addEventListener('message', this.closeInterstitialListener, false)
    }

    const origin = getMessageOrigin(this.targetOrigin)
    this.targetWindow.postMessage(JSON.stringify(request), origin)
  }

  private closeInterstitialListener = (event: MessageEvent): void => {
    try {
      if (
        event.data === 'closeHostedInterstitial'
      ) {
        this.window.removeEventListener('message', this.closeInterstitialListener)
        if (this.closeInterstitialCallback) {
          this.closeInterstitialCallback()
          delete this.closeInterstitialCallback
        }
      }
    // eslint-disable-next-line no-empty
    } catch (e) { }
    return null
  }

  private handleMessage(event: MessageEvent) {
    if (event.origin !== this.targetOrigin) {
      return
    }

    let response: RpcResponse

    try {
      response = JSON.parse(event.data)
    } catch (ex) {
      return
    }

    if (!(response.id in this.callbacks)) {
      return
    }

    const callbackData = this.callbacks[response.id]
    clearTimeout(callbackData.timeoutHandle)

    if (response.error) {
      callbackData.callback(undefined, response.error)
    } else {
      callbackData.callback(response.result)
    }
  }
}
